import { asArray, asRecord } from '@/lib/as-array';

export type ContractClientParty = {
  name: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
};

export type ContractSignatureRecord = {
  clientName?: string | null;
  signedAt?: string | null;
  signatureUrl?: string | null;
  slot?: number | null;
  clauseInitials?:
    | Array<{ clauseId?: string; text?: string }>
    | Record<string, { text?: string } | string>
    | null;
};

export type PublicSigningSession = {
  activeSignerId?: string | null;
  activeSlot?: number | null;
  canSign?: boolean;
  clientSigners?: Array<{
    id: string;
    name: string;
    status: string;
    slot: number;
  }>;
};

const PORTAL_SIGNED_STORAGE_PREFIX = 'larkpm.portal-signed.';

export function portalSignedStorageKey(token: string): string {
  return `${PORTAL_SIGNED_STORAGE_PREFIX}${token}`;
}

export function readPortalSignedLock(token: string | undefined): boolean {
  if (!token || typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(portalSignedStorageKey(token)) === '1';
  } catch {
    return false;
  }
}

export function writePortalSignedLock(token: string | undefined): void {
  if (!token || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(portalSignedStorageKey(token), '1');
  } catch {
    // private mode / quota — the thank-you state still hides remaining pads
  }
}

const CLIENT_PADS_LOCKED_STATUSES = new Set([
  'CLIENT_SIGNED',
  'EXECUTED',
  'EXECUTED_PAPER',
  'WON',
  'LOST',
  'DECLINED',
  'VOIDED',
  'EXPIRED',
]);

/** Which client pad the public portal may unlock. Null = view only. */
export function activePublicSignSlot(input: {
  parties: [ContractClientParty, ContractClientParty];
  signatures?: ContractSignatureRecord[];
  session?: PublicSigningSession | null;
  sessionComplete?: boolean;
  status?: string;
}): 1 | 2 | null {
  if (input.sessionComplete) return null;
  if (input.status && CLIENT_PADS_LOCKED_STATUSES.has(input.status)) return null;
  if (input.session?.canSign === false) return null;
  if (input.session?.activeSlot === 1 || input.session?.activeSlot === 2) {
    return input.session.activeSlot;
  }
  // Sequential machine: after client 1 signs, only client 2 is open —
  // even if they typed a different legal name than the party card.
  if (input.status === 'PARTIALLY_SIGNED') return 2;
  const rows = asArray<ContractSignatureRecord>(input.signatures);
  if (rows[0]) return input.parties[1].name ? 2 : null;
  return 1;
}

export function isPublicClientSlotSigned(
  slot: 1 | 2,
  parties: [ContractClientParty, ContractClientParty],
  signatures: ContractSignatureRecord[] | undefined,
  session?: PublicSigningSession | null,
  status?: string,
): boolean {
  if (status && CLIENT_PADS_LOCKED_STATUSES.has(status)) return true;
  if (status === 'PARTIALLY_SIGNED' && slot === 1) return true;
  const fromSession = session?.clientSigners?.find((signer) => signer.slot === slot);
  if (fromSession) return fromSession.status === 'SIGNED';
  if (asArray<ContractSignatureRecord>(signatures)[slot - 1]) return true;
  return !!findClientSignature(signatures, parties[slot - 1].name);
}

export function signatureForPublicSlot(
  slot: 1 | 2,
  parties: [ContractClientParty, ContractClientParty],
  signatures: ContractSignatureRecord[] | undefined,
  session?: PublicSigningSession | null,
): ContractSignatureRecord | undefined {
  const rows = asArray<ContractSignatureRecord>(signatures);
  const fromSession = session?.clientSigners?.find((signer) => signer.slot === slot);
  return (
    findClientSignature(signatures, fromSession?.name ?? null) ||
    findClientSignature(signatures, parties[slot - 1].name) ||
    rows[slot - 1]
  );
}

export const OTHER_CLIENT_WAITS_FOR_OWN_LINK =
  'This person signs from their own email link. You can only complete your own signature.';

function text(value: unknown): string | null {
  if (value == null) return null;
  const next = String(value).trim();
  return next.length ? next : null;
}

/** Role labels / sample copy — not a legal name the signer typed. */
const GUIDANCE_SIGNING_NAME = /^(client\s*\d+(\s+full\s+name)?|printed name)$/i;

export function isSigningNameGuidance(value: string | null | undefined): boolean {
  const next = (value ?? '').trim();
  if (!next) return true;
  return GUIDANCE_SIGNING_NAME.test(next);
}

/** Empty string when the value is placeholder/guidance; otherwise the typed legal name. */
export function signingNameInputValue(value: string | null | undefined): string {
  return isSigningNameGuidance(value) ? '' : String(value ?? '').trim();
}

/**
 * First keystroke replaces sample copy instead of appending to it.
 * Real stored legal names are left intact (caller still receives the raw next value).
 */
export function nextSigningNameInput(
  previous: string,
  next: string,
  placeholder?: string,
): string {
  const sample = (placeholder ?? '').trim();
  const wasGuidance =
    isSigningNameGuidance(previous) ||
    (!!sample && previous.trim().toLowerCase() === sample.toLowerCase());
  if (!wasGuidance) return next;
  if (previous && next.startsWith(previous)) return next.slice(previous.length);
  return next;
}

function personName(row: Record<string, unknown> | null): string | null {
  if (!row) return null;
  return (
    text(row.fullName) ||
    text(row.name) ||
    [text(row.firstName), text(row.lastName)].filter(Boolean).join(' ').trim() ||
    text(row.businessName)
  );
}

function clientAddress(row: Record<string, unknown> | null): string | null {
  if (!row) return null;
  return (
    text(row.address) ||
    [
      text(row.streetAddress1) || text(row.addressLine1),
      text(row.streetAddress2) || text(row.addressLine2),
      [text(row.city), text(row.state), text(row.zipCode) || text(row.postalCode)]
        .filter(Boolean)
        .join(', '),
    ]
      .filter(Boolean)
      .join(', ') ||
    null
  );
}

function fromContactRow(row: Record<string, unknown> | null): ContractClientParty {
  return {
    name: personName(row),
    email: text(row?.email),
    phone: text(row?.contactNumber) || text(row?.phone),
    address: clientAddress(row),
  };
}

/** Always two client slots — husband/wife or primary + additional person. */
export function collectClientParties(input: unknown): [ContractClientParty, ContractClientParty] {
  const named = collectClientContacts(input);
  const empty: ContractClientParty = { name: null, email: null, phone: null, address: null };
  return [named[0] ?? empty, named[1] ?? empty];
}

/** Every signing client with their own name and contact information. */
export function collectClientContacts(input: unknown): ContractClientParty[] {
  const root = asRecord(input) ?? {};
  const data = asRecord(root.contractData) ?? root;
  const frozen = asRecord(data.client);
  const related = asRecord(asRecord(root.project)?.client) ?? asRecord(root.client);
  const primary = frozen ?? related ?? {};
  const contacts = asArray(
    primary.contactPersons ?? related?.contactPersons ?? frozen?.contactPersons,
  );
  const signatures = asArray<ContractSignatureRecord>(
    root.clientSignatures ?? data.clientSignatures,
  );

  const person1: ContractClientParty = {
    name: personName(primary) || personName(related),
    email:
      text(primary.email) ||
      text(primary.primaryEmail) ||
      text(related?.email) ||
      text(related?.primaryEmail),
    phone:
      text(primary.phone) ||
      text(primary.primaryContactNumber) ||
      text(related?.phone) ||
      text(related?.primaryContactNumber),
    address: clientAddress(primary) || clientAddress(related),
  };

  const extras = contacts
    .map((row) => fromContactRow(asRecord(row)))
    .filter((person) => person.name || person.email || person.phone);

  if (extras.length === 0) {
    const fallbackName =
      text(primary.secondPerson) ||
      text(primary.person2) ||
      text(signatures[1]?.clientName);
    if (fallbackName) extras.push({ name: fallbackName, email: null, phone: null, address: null });
  }

  return person1.name || person1.email || person1.phone ? [person1, ...extras] : extras;
}

export function signingTermsFromHtml(
  html: string | null | undefined,
  contractorName?: string | null,
): string[] {
  const raw = (html || '').trim();
  if (!raw) return defaultSigningTerms(contractorName);
  const withBreaks = raw
    .replace(/<\/(p|li|div|h[1-6])>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '');
  const lines = withBreaks
    .split(/\n+/)
    .map((line) => line.replace(/^\s*\d+[.)]\s*/, '').replace(/&nbsp;/g, ' ').trim())
    .filter(Boolean);
  const terms = lines.length ? lines : defaultSigningTerms(contractorName);
  const company = text(contractorName) || 'the Contractor';
  return terms.map((term) => term.replace(/\{\{\s*companyName\s*\}\}/g, company));
}

export function defaultSigningTerms(contractorName?: string | null): string[] {
  const contractor = text(contractorName) || 'the Contractor';
  return [
    'Client has reviewed the Scope of Work and agrees the selections and specifications encompass the entire agreement.',
    'Client agrees to the use of electronic signatures for entering into this Contract and agreeing to the selections, standards, and clauses noted herein.',
    'A copy of the contract is provided digitally upon signature and completion. If a glitch occurs and the auto-provided contract contains specifications, notes, or details that are partially cut-off, scrambled, or not readable, the Contractor will upon request provide a readable copy. Any technological issues do not nullify the contract or negate either party from executing the contract or the requirements stated therein.',
    `Client affirms ownership of the residence and possesses the lawful right as the owner or documented, legal representative of all owners to authorize ${contractor} to access and modify the structure and property.`,
  ];
}

export function findClientSignature(
  signatures: ContractSignatureRecord[] | undefined,
  name: string | null,
): ContractSignatureRecord | undefined {
  const needle = text(name)?.toLowerCase();
  if (!needle) return undefined;
  return asArray<ContractSignatureRecord>(signatures).find(
    (row) => text(row.clientName)?.toLowerCase() === needle,
  );
}

function namesMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = text(a)?.toLowerCase();
  const right = text(b)?.toLowerCase();
  return !!left && !!right && left === right;
}

function rowHasInk(row: ContractSignatureRecord | undefined): boolean {
  return Boolean(text(row?.signatureUrl) || text(row?.clientName) || text(row?.signedAt));
}

/**
 * CLIENT signer rows (portal / staff) that carry a file URL not already in
 * clientSignatures[]. Staff GET today omits signers; keep this so a later
 * payload still paints Client 2.
 */
export function collectClientInkRows(input: unknown): ContractSignatureRecord[] {
  const root = asRecord(input);
  const rows = asArray<ContractSignatureRecord>(
    root?.clientSignatures ?? (Array.isArray(input) ? input : []),
  ).map((row) => ({ ...row }));
  const seen = new Set(
    rows.map((row) => text(row.signatureUrl)).filter((url): url is string => !!url),
  );
  const signerBags = [
    root?.signers,
    root?.clientSigners,
    asRecord(root?.signingSession)?.clientSigners,
  ];
  for (const bag of signerBags) {
    for (const raw of asArray(bag)) {
      const rec = asRecord(raw);
      if (!rec) continue;
      const role = text(rec.role)?.toUpperCase();
      if (role && role !== 'CLIENT') continue;
      const status = text(rec.status)?.toUpperCase();
      if (status && status !== 'SIGNED') continue;
      const url = text(rec.signatureUrl);
      if (!url || seen.has(url)) continue;
      seen.add(url);
      rows.push({
        clientName: text(rec.name) || text(rec.clientName),
        signatureUrl: url,
        signedAt: text(rec.signedAt),
        slot: typeof rec.slot === 'number' ? rec.slot : null,
      });
    }
  }
  return rows;
}

/**
 * Pair unique ink rows to Client 1 / Client 2.
 *
 * Independent `signatureForParty` calls used to blank Client 2 when a later
 * row's clientName matched the Client 1 party card (dress CRT-ZAFX7V7KN7:
 * Bob tomato, Mr. Test Client, asdfsd vs cards Mr. Test Client / Mrs. Test).
 * Slot order wins first; leftover files still paint Client 2.
 */
export function assignClientSignatureSlots(
  signatures: ContractSignatureRecord[] | undefined,
  partyNames: [string | null | undefined, string | null | undefined],
): [ContractSignatureRecord | undefined, ContractSignatureRecord | undefined] {
  const rows = asArray<ContractSignatureRecord>(signatures);
  const used = new Set<number>();

  const claim = (index: number) => {
    used.add(index);
    return rows[index];
  };

  const unusedNameIndex = (name: string | null | undefined) => {
    const needle = text(name)?.toLowerCase();
    if (!needle) return -1;
    return rows.findIndex(
      (row, index) => !used.has(index) && text(row.clientName)?.toLowerCase() === needle,
    );
  };

  const pick = (
    index: number,
    name: string | null | undefined,
    otherName: string | null | undefined,
  ) => {
    const atSlot = rows[index];
    if (atSlot && !used.has(index) && !namesMatch(atSlot.clientName, otherName)) {
      return claim(index);
    }
    const named = unusedNameIndex(name);
    if (named >= 0) return claim(named);
    const leftover = rows.findIndex((row, rowIndex) => !used.has(rowIndex) && rowHasInk(row));
    if (leftover >= 0) return claim(leftover);
    return undefined;
  };

  return [pick(0, partyNames[0], partyNames[1]), pick(1, partyNames[1], partyNames[0])];
}

/**
 * Slot ink: unique row per party. Prefer this slot's row when it is not the
 * other party's printed name, then an unused exact name, then any leftover
 * file. Blank Client 2 only when no second signature file remains.
 */
export function signatureForParty(
  signatures: ContractSignatureRecord[] | undefined,
  name: string | null,
  index: number,
  otherPartyName?: string | null,
): ContractSignatureRecord | undefined {
  const slot = index === 1 ? 1 : 0;
  const names: [string | null | undefined, string | null | undefined] =
    slot === 1 ? [otherPartyName, name] : [name, otherPartyName];
  return assignClientSignatureSlots(signatures, names)[slot];
}

export function formatSignatureDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
}

/** Which named client may sign now. Client 2 waits until Client 1 is signed. */
export function nextUnsignedClientIndex(
  parties: [ContractClientParty, ContractClientParty],
  signatures: ContractSignatureRecord[] | undefined,
): 0 | 1 | null {
  const [first, second] = assignClientSignatureSlots(signatures, [parties[0].name, parties[1].name]);
  if (!first) return 0;
  if (parties[1].name && !second) return 1;
  return null;
}

export function remainingClientMustSign(
  parties: [ContractClientParty, ContractClientParty],
  signatures: ContractSignatureRecord[] | undefined,
): boolean {
  return nextUnsignedClientIndex(parties, signatures) !== null;
}

/** Read-only signature pads for View (list inspector + staff paper). */
export type ViewSignatureSlot = {
  key: string;
  role: string;
  name: string | null;
  signedAt: string | null;
  signatureImage: string | null;
  waitNote?: string | null;
};

const WON_CHIP_STATUSES = new Set(['WON', 'EXECUTED', 'EXECUTED_PAPER']);

function viewSlotFromRow(
  key: string,
  role: string,
  name: string | null,
  row?: ContractSignatureRecord,
): ViewSignatureSlot {
  return {
    key,
    role,
    name: name || text(row?.clientName),
    signedAt: formatSignatureDate(row?.signedAt),
    signatureImage: text(row?.signatureUrl),
  };
}

/**
 * Client 1, Client 2 when that party exists, leftover stored ink as extra
 * pads, then Contractor. Blank contractor is correct when company never
 * signed — including when the chip says Won because Mark won ran first.
 */
export function viewSignatureSlots(
  contract: unknown,
  extra?: { contractorName?: string | null },
): ViewSignatureSlot[] {
  const root = asRecord(contract) ?? {};
  const parties = collectClientParties(contract);
  const rows = collectClientInkRows(contract);
  const [client1, client2] = assignClientSignatureSlots(rows, [parties[0].name, parties[1].name]);
  const used = new Set<number>();
  const markUsed = (row?: ContractSignatureRecord) => {
    if (!row) return;
    const index = rows.indexOf(row);
    if (index >= 0) used.add(index);
  };
  markUsed(client1);
  markUsed(client2);
  const contractorName =
    extra?.contractorName ||
    text(asRecord(root.creator)?.fullName) ||
    text(asRecord(root.companySigner)?.fullName) ||
    text(asRecord(root.salesRep)?.name) ||
    null;
  const companyImage = text(root.companySignature);
  const slots: ViewSignatureSlot[] = [
    viewSlotFromRow('client-1', 'Client 1', parties[0].name || client1?.clientName || null, client1),
  ];
  if (parties[1].name || client2) {
    slots.push(
      viewSlotFromRow('client-2', 'Client 2', parties[1].name || client2?.clientName || null, client2),
    );
  }
  let extraIndex = 1;
  rows.forEach((row, index) => {
    if (used.has(index) || !text(row.signatureUrl)) return;
    used.add(index);
    slots.push(
      viewSlotFromRow(
        `client-extra-${extraIndex}`,
        extraIndex === 1 ? 'Additional signature' : `Additional signature ${extraIndex}`,
        text(row.clientName),
        row,
      ),
    );
    extraIndex += 1;
  });
  slots.push({
    key: 'contractor',
    role: 'Contractor',
    name: contractorName,
    signedAt: formatSignatureDate(text(root.companySignedAt)),
    signatureImage: companyImage,
    waitNote:
      !companyImage && WON_CHIP_STATUSES.has(text(root.status) || '')
        ? 'Company has not countersigned yet.'
        : null,
  });
  return slots;
}

export function viewSlotsHaveInk(slots: ViewSignatureSlot[]): boolean {
  return slots.some((slot) => Boolean(slot.signatureImage));
}

/** Render a typed legal name as a cursive PNG the same way a drawn stroke is stored. */
export function typedSignatureDataUrl(
  name: string,
  width = 440,
  height = 160,
): string {
  const canvas = document.createElement('canvas');
  canvas.width = width * 2;
  canvas.height = height * 2;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.scale(2, 2);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#1a1a1a';
  const size = Math.max(22, Math.round(height * 0.36));
  ctx.font = `italic ${size}px "Segoe Script", "Brush Script MT", "Apple Chancery", cursive`;
  ctx.textBaseline = 'middle';
  ctx.fillText(name.trim(), 18, height / 2, width - 36);
  return canvas.toDataURL('image/png');
}
